import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Send, Calendar, User, MessageSquare, Building2, CheckCircle } from 'lucide-react';
import { Product } from '../types';
import { supabase } from '../lib/supabaseClient';
import {
  trackQuoteFormClosed,
  trackQuoteFormError,
  trackQuoteFormStarted,
  trackQuoteFormSubmitted,
  trackQuoteModalOpen,
  getAttributionForBooking,
  trackQuoteRequest,
  type QuoteFormCloseMethod,
} from '../utils/siteAnalytics';
import { pushToDataLayer } from '../utils/ga4Ecommerce';
import DatePicker from './DatePicker';
import { formatDateLocal } from '../utils/dateUtils';
import B2bEventFormSections, { type B2bFormData } from './b2b/B2bEventFormSections';
import { B2B_EVENT_TYPES } from '../config/b2bEventTypes';
import {
  buildQuoteAttributionSnapshot,
  hubNeedCardFromCtaContext,
  resolvePartnerLeadFromWindow,
  type PartnerLeadAttribution,
} from '../utils/partnerAttribution';
import { getProductMinQuantity } from '../utils/bookingFlowProducts';
import {
  buildFindOrCreateCustomerArgs,
  isUnknownCustomerRpcArg,
  legacyFindOrCreateCustomerArgs,
} from '../utils/customerRegisterLink';

const EVENT_TYPES = [
  'Bröllop',
  'Företagsevent',
  'Fest',
  'Konferens / presentation',
  'Student',
  'Annat',
] as const;

type EventTypeOption = (typeof EVENT_TYPES)[number];

interface CustomerQuoteRequestFormProps {
  product?: Product;
  onClose: () => void;
  packageType?: string;
  variant?: 'modal' | 'inline' | 'b2b_event';
  defaultEventType?: EventTypeOption | string;
  /** YYYY-MM-DD */
  defaultEventDate?: string;
  defaultDateUndecided?: boolean;
  partnerLead?: PartnerLeadAttribution;
  showSkaneEventBanner?: boolean;
}

function buildB2bCustomerMessage(data: B2bFormData, dateUndecided: boolean): string {
  const lines: string[] = [];
  if (data.companyName.trim()) lines.push(`Företag: ${data.companyName.trim()}`);
  if (data.needs.length) lines.push(`Behov: ${data.needs.join(', ')}`);
  if (data.budgetSegment) lines.push(`Budget: ${data.budgetSegment}`);
  if (data.fullServicePreference) lines.push(`Helhetsansvar: ${data.fullServicePreference}`);
  if (data.venue.trim()) lines.push(`Venue/lokal: ${data.venue.trim()}`);
  if (dateUndecided) lines.push('(Datum ännu inte bestämt)');
  if (data.message.trim()) {
    if (lines.length) lines.push('');
    lines.push(data.message.trim());
  }
  return lines.join('\n') || 'B2B eventförfrågan';
}

function createInitialB2bForm(defaultEventType?: string): B2bFormData {
  const eventType =
    defaultEventType && (B2B_EVENT_TYPES as readonly string[]).includes(defaultEventType)
      ? defaultEventType
      : '';
  return {
    companyName: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCity: '',
    venue: '',
    eventDate: '',
    eventEndDate: '',
    eventType,
    guestCount: '',
    needs: [],
    fullServicePreference: '',
    budgetSegment: '',
    message: '',
  };
}

function inferEventType(
  defaultEventType: string | undefined,
  packageType: string | undefined,
  pathname: string
): string {
  if (defaultEventType && EVENT_TYPES.includes(defaultEventType as EventTypeOption)) {
    return defaultEventType;
  }

  const haystack = `${packageType ?? ''} ${pathname}`.toLowerCase();

  if (
    haystack.includes('bröllop') ||
    haystack.includes('brollop') ||
    haystack.includes('wedding')
  ) {
    return 'Bröllop';
  }
  if (
    haystack.includes('student') ||
    haystack.includes('studentflak') ||
    haystack.includes('studentfest')
  ) {
    return 'Student';
  }
  if (
    haystack.includes('konferens') ||
    haystack.includes('presentation') ||
    haystack.includes('mässa') ||
    haystack.includes('massa')
  ) {
    return 'Konferens / presentation';
  }
  if (
    haystack.includes('företag') ||
    haystack.includes('foretag') ||
    haystack.includes('business') ||
    haystack.includes('julfest') ||
    haystack.includes('eventteknik') ||
    haystack.includes('scen')
  ) {
    return 'Företagsevent';
  }
  if (haystack.includes('fest') || haystack.includes('kompletta')) {
    return 'Fest';
  }

  return '';
}

function messagePlaceholder(eventType: string): string {
  if (eventType === 'Bröllop') {
    return 'Berätta kort om ert bröllop och vad ni vill ha hjälp med. Det behöver inte vara detaljerat – vi hjälper er att hitta rätt lösning.';
  }
  if (
    eventType === 'Företagsevent' ||
    eventType === 'Konferens / presentation'
  ) {
    return 'Berätta kort om eventet och vad ni behöver hjälp med. Det behöver inte vara detaljerat – vi hjälper er att hitta rätt lösning.';
  }
  return 'Berätta kort om ert event och vad ni behöver hjälp med. Det behöver inte vara detaljerat – vi hjälper er att hitta rätt lösning.';
}

const inputClass = (hasError?: boolean) =>
  `w-full p-2.5 bg-gray-900 border ${
    hasError ? 'border-red-500' : 'border-gray-600'
  } text-white rounded-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500`;

const CustomerQuoteRequestForm: React.FC<CustomerQuoteRequestFormProps> = ({
  product,
  onClose,
  packageType,
  variant = 'modal',
  defaultEventType,
  defaultEventDate,
  defaultDateUndecided = false,
  partnerLead: partnerLeadProp,
  showSkaneEventBanner = false,
}) => {
  const isB2bEvent = variant === 'b2b_event';
  const isInline = variant === 'inline' || isB2bEvent;
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '';
  const partnerLead = useMemo(
    () => partnerLeadProp ?? resolvePartnerLeadFromWindow(),
    [partnerLeadProp]
  );

  const inferredEventType = useMemo(
    () => inferEventType(defaultEventType, packageType, pathname),
    [defaultEventType, packageType, pathname]
  );

  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    customerCity: '',
    eventDate: defaultDateUndecided ? '' : (defaultEventDate || ''),
    eventEndDate: '',
    eventType: inferredEventType,
    guestCount: '',
    message: '',
  });
  const [b2bFormData, setB2bFormData] = useState(() =>
    createInitialB2bForm(defaultEventType)
  );
  const [dateUndecided, setDateUndecided] = useState(Boolean(defaultDateUndecided));
  const [showEndDate, setShowEndDate] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [customerEmailFailed, setCustomerEmailFailed] = useState(false);
  const formStartedRef = useRef(false);
  const successRef = useRef(false);

  const activeEventType = isB2bEvent ? b2bFormData.eventType : formData.eventType;

  const analyticsCtx = useMemo(
    () => ({
      productId: product?.id ?? null,
      productName: product?.name ?? null,
      packageType: packageType ?? null,
      eventType: activeEventType || null,
      variant,
      partner: partnerLead.partner ?? null,
      budgetSegment: isB2bEvent ? b2bFormData.budgetSegment || null : null,
      ctaContext: partnerLead.cta_context ?? null,
    }),
    [
      product?.id,
      product?.name,
      packageType,
      activeEventType,
      variant,
      partnerLead.partner,
      partnerLead.cta_context,
      isB2bEvent,
      b2bFormData.budgetSegment,
    ]
  );

  useEffect(() => {
    if (!isB2bEvent) {
      trackQuoteModalOpen(analyticsCtx);
    }
    // Only fire open once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    successRef.current = success;
  }, [success]);

  const markFormStarted = () => {
    if (formStartedRef.current) return;
    formStartedRef.current = true;
    trackQuoteFormStarted(analyticsCtx);
  };

  const handleClose = (method: QuoteFormCloseMethod) => {
    if (!successRef.current) {
      trackQuoteFormClosed({ ...analyticsCtx, closeMethod: method });
    }
    onClose();
  };

  const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone: string) => /^[\d\s\-+()]{8,}$/.test(phone);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (isB2bEvent) {
      if (!b2bFormData.companyName.trim()) {
        newErrors.companyName = 'Företagsnamn är obligatoriskt';
      }
      if (!b2bFormData.customerName.trim()) {
        newErrors.customerName = 'Kontaktperson är obligatoriskt';
      }
      if (!b2bFormData.customerEmail.trim()) {
        newErrors.customerEmail = 'E-post är obligatoriskt';
      } else if (!validateEmail(b2bFormData.customerEmail)) {
        newErrors.customerEmail = 'Ogiltig e-postadress';
      }
      if (b2bFormData.customerPhone.trim() && !validatePhone(b2bFormData.customerPhone)) {
        newErrors.customerPhone = 'Ogiltigt telefonnummer';
      }
      if (!b2bFormData.guestCount.trim() || Number(b2bFormData.guestCount) < 1) {
        newErrors.guestCount = 'Ange antal gäster';
      }
      if (!dateUndecided) {
        if (!b2bFormData.eventDate) {
          newErrors.eventDate = 'Ange datum eller markera att det inte är bestämt ännu';
        } else {
          const selectedDate = new Date(b2bFormData.eventDate);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          if (selectedDate < today) {
            newErrors.eventDate = 'Datum måste vara idag eller i framtiden';
          }
        }
      }
      if (!dateUndecided && b2bFormData.eventEndDate && b2bFormData.eventDate) {
        const startDate = new Date(b2bFormData.eventDate);
        const endDate = new Date(b2bFormData.eventEndDate);
        if (endDate < startDate) {
          newErrors.eventEndDate = 'Slutdatum måste vara efter startdatum';
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    }

    if (!formData.customerName.trim()) {
      newErrors.customerName = 'Namn är obligatoriskt';
    }

    if (!formData.customerEmail.trim()) {
      newErrors.customerEmail = 'E-post är obligatoriskt';
    } else if (!validateEmail(formData.customerEmail)) {
      newErrors.customerEmail = 'Ogiltig e-postadress';
    }

    if (formData.customerPhone.trim() && !validatePhone(formData.customerPhone)) {
      newErrors.customerPhone = 'Ogiltigt telefonnummer';
    }

    if (!dateUndecided) {
      if (!formData.eventDate) {
        newErrors.eventDate = 'Ange datum eller markera att det inte är bestämt ännu';
      } else {
        const selectedDate = new Date(formData.eventDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          newErrors.eventDate = 'Datum måste vara idag eller i framtiden';
        }
      }
    }

    if (!dateUndecided && formData.eventEndDate && formData.eventDate) {
      const startDate = new Date(formData.eventDate);
      const endDate = new Date(formData.eventEndDate);
      if (endDate < startDate) {
        newErrors.eventEndDate = 'Slutdatum måste vara efter startdatum';
      }
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Berätta kort vad ni behöver hjälp med';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const generateQuoteNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `OFF-${year}${month}${day}-${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);

    try {
      const quoteId = crypto.randomUUID();
      const quoteNumber = generateQuoteNumber();
      const bookingAttribution = getAttributionForBooking();

      let quoteData: Record<string, unknown>;

      if (isB2bEvent) {
        const eventDate = dateUndecided ? null : b2bFormData.eventDate;
        const eventEndDate = dateUndecided ? null : b2bFormData.eventEndDate || null;
        const customerMessage = buildB2bCustomerMessage(b2bFormData, dateUndecided);
        const hubNeedCard = hubNeedCardFromCtaContext(partnerLead.cta_context);
        const requestMetadata = {
          company_name: b2bFormData.companyName.trim() || null,
          venue: b2bFormData.venue.trim() || null,
          needs: b2bFormData.needs,
          full_service_preference: b2bFormData.fullServicePreference || null,
          budget_segment: b2bFormData.budgetSegment || null,
          ...(hubNeedCard ? { hub_need_card: hubNeedCard } : {}),
        };
        const attributionSnapshot = buildQuoteAttributionSnapshot({
          partnerLead,
          analyticsSnapshot: bookingAttribution.attribution_snapshot ?? undefined,
          formOverrides: {
            budget_segment: b2bFormData.budgetSegment || null,
            event_type: b2bFormData.eventType || null,
            full_service_preference: b2bFormData.fullServicePreference || null,
          },
          packageType: packageType ?? null,
          productId: product?.id ?? null,
          formVariant: 'b2b_event',
          hubNeedCard: hubNeedCard ?? null,
          ctaType: partnerLead.cta_context ?? partnerLead.cta_type ?? null,
        });

        quoteData = {
          id: quoteId,
          quote_number: quoteNumber,
          customer_name: b2bFormData.customerName.trim(),
          customer_email: b2bFormData.customerEmail.trim(),
          customer_phone: b2bFormData.customerPhone.trim() || null,
          customer_organization_number: null,
          customer_address: null,
          customer_postal_code: null,
          customer_city: b2bFormData.customerCity.trim() || null,
          customer_billing_address: null,
          customer_billing_postal_code: null,
          customer_billing_city: null,
          event_date: eventDate,
          event_end_date: eventEndDate,
          event_type: b2bFormData.eventType || null,
          event_description: b2bFormData.eventType || null,
          guest_count: parseInt(b2bFormData.guestCount, 10),
          customer_message: customerMessage,
          delivery_address: b2bFormData.venue.trim() || null,
          items: JSON.stringify([
            {
              id: crypto.randomUUID(),
              product_id: null,
              product_name: packageType || 'B2B eventförfrågan',
              description: 'Eventlösning efter behovsbaserad förfrågan',
              quantity: 1,
              unit_price: 0,
              total_price: 0,
              is_custom: true,
            },
          ]),
          subtotal: 0,
          discount_percentage: 0,
          discount_amount: 0,
          delivery_fee: 0,
          total_amount: 0,
          status: 'draft',
          source: 'customer_request',
          requested_product_id: product?.id || null,
          attribution_snapshot: attributionSnapshot,
          request_metadata: requestMetadata,
        };
      } else {
        const eventDate = dateUndecided ? null : formData.eventDate;
        const eventEndDate = dateUndecided ? null : formData.eventEndDate || null;
        const messageWithDateHint =
          dateUndecided && !formData.message.toLowerCase().includes('datum')
            ? `${formData.message.trim()}\n\n(Datum ännu inte bestämt)`
            : formData.message.trim();

        const attributionSnapshot = buildQuoteAttributionSnapshot({
          partnerLead: {
            ...partnerLead,
            source_page:
              partnerLead.source_page ||
              (typeof window !== 'undefined'
                ? window.location.pathname.split('?')[0]
                : pathname?.split('?')[0]) ||
              null,
            landing_page:
              partnerLead.landing_page ||
              partnerLead.source_page ||
              null,
          },
          analyticsSnapshot: bookingAttribution.attribution_snapshot ?? undefined,
          formOverrides: {
            event_type: formData.eventType || null,
          },
          packageType: packageType ?? null,
          productId: product?.id ?? null,
          formVariant: variant === 'inline' ? 'inline' : 'modal',
          ctaType: partnerLead.cta_type ?? partnerLead.cta_context ?? null,
        });

        quoteData = {
          id: quoteId,
          quote_number: quoteNumber,
          customer_name: formData.customerName.trim(),
          customer_email: formData.customerEmail.trim(),
          customer_phone: formData.customerPhone.trim() || null,
          customer_organization_number: null,
          customer_address: null,
          customer_postal_code: null,
          customer_city: formData.customerCity.trim() || null,
          customer_billing_address: null,
          customer_billing_postal_code: null,
          customer_billing_city: null,
          event_date: eventDate,
          event_end_date: eventEndDate,
          event_type: formData.eventType || null,
          event_description: formData.eventType || null,
          guest_count: formData.guestCount ? parseInt(formData.guestCount, 10) : null,
          customer_message: messageWithDateHint,
          items: JSON.stringify(
            product
              ? [
                  {
                    id: crypto.randomUUID(),
                    product_id: product.id,
                    product_name: product.name,
                    description: product.shortDescription,
                    quantity: getProductMinQuantity(product),
                    unit_price: 0,
                    total_price: 0,
                    is_custom: false,
                    image: product?.image ?? undefined,
                  },
                ]
              : [
                  {
                    id: crypto.randomUUID(),
                    product_id: null,
                    product_name: packageType || 'Företagspaket',
                    description: 'Paket efter offertförfrågan',
                    quantity: 1,
                    unit_price: 0,
                    total_price: 0,
                    is_custom: true,
                  },
                ]
          ),
          subtotal: 0,
          discount_percentage: 0,
          discount_amount: 0,
          delivery_fee: 0,
          total_amount: 0,
          status: 'draft',
          source: 'customer_request',
          requested_product_id: product?.id || null,
          attribution_snapshot: attributionSnapshot,
        };
      }

      if (isB2bEvent) {
        try {
          const linkArgs = buildFindOrCreateCustomerArgs({
            email: b2bFormData.customerEmail,
            name: b2bFormData.companyName,
            phone: b2bFormData.customerPhone,
            city: b2bFormData.customerCity,
            customerType: 'business',
            contactName: b2bFormData.customerName,
          });
          if (linkArgs) {
            const created = await supabase.rpc('find_or_create_customer', linkArgs);
            let linkError = created.error;
            let customerId = created.data;
            if (linkError && isUnknownCustomerRpcArg(linkError.message || '')) {
              const retry = await supabase.rpc(
                'find_or_create_customer',
                legacyFindOrCreateCustomerArgs(linkArgs),
              );
              linkError = retry.error;
              customerId = retry.data;
            }
            if (!linkError && customerId) {
              quoteData.customer_id = customerId;
            } else if (linkError) {
              console.warn('Kunde inte spara företagskund från förfrågan:', linkError.message);
            }
          }
        } catch (linkErr) {
          console.warn('Kunde inte spara företagskund från förfrågan:', linkErr);
        }
      }

      // Client-supplied id: avoid .select() (anon has no SELECT on quotes).
      let { error: quoteError } = await supabase.from('quotes').insert(quoteData);

      if (
        quoteError &&
        typeof quoteError.message === 'string' &&
        quoteError.message.includes('customer_id')
      ) {
        const { customer_id: _customerId, ...withoutCustomer } = quoteData;
        const retry = await supabase.from('quotes').insert(withoutCustomer);
        quoteError = retry.error;
      }

      if (
        quoteError &&
        typeof quoteError.message === 'string' &&
        quoteError.message.includes('request_metadata')
      ) {
        const { request_metadata: _meta, ...withoutMetadata } = quoteData;
        const retry = await supabase.from('quotes').insert(withoutMetadata);
        quoteError = retry.error;
      }

      if (
        quoteError &&
        typeof quoteError.message === 'string' &&
        quoteError.message.includes('attribution_snapshot')
      ) {
        const { attribution_snapshot: _snap, ...withoutAttribution } = quoteData;
        const retry = await supabase.from('quotes').insert(withoutAttribution);
        quoteError = retry.error;
      }

      if (quoteError) throw quoteError;
      const quote = { id: quoteId };

      setCustomerEmailFailed(false);
      try {
        const emailResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              type: 'quote_request_admin',
              quoteId: quote.id,
            }),
          }
        );

        if (!emailResponse.ok) {
          console.error(
            JSON.stringify({
              tag: 'ADMIN_NOTIFICATION_FAILURE',
              email_type: 'quote_request_admin',
              quote_id: quote.id,
              http_status: emailResponse.status,
              source: 'CustomerQuoteRequestForm',
            })
          );
        }

        const customerEmailResponse = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-email`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
            },
            body: JSON.stringify({
              type: 'quote_request_customer',
              quoteId: quote.id,
            }),
          }
        );

        if (!customerEmailResponse.ok) {
          console.error(
            JSON.stringify({
              tag: 'CUSTOMER_NOTIFICATION_FAILURE',
              email_type: 'quote_request_customer',
              quote_id: quote.id,
              http_status: customerEmailResponse.status,
              source: 'CustomerQuoteRequestForm',
            })
          );
          setCustomerEmailFailed(true);
        }
      } catch {
        console.error(
          JSON.stringify({
            tag: 'ADMIN_NOTIFICATION_FAILURE',
            email_type: 'quote_request_admin',
            quote_id: quote.id,
            error_type: 'network_or_exception',
            source: 'CustomerQuoteRequestForm',
          })
        );
        setCustomerEmailFailed(true);
      }

      setSuccess(true);
      successRef.current = true;

      const listPrice =
        product && !product.priceOnRequest && Number(product.pricePerDay) > 0
          ? Number(product.pricePerDay)
          : 0;

      pushToDataLayer({
        event: 'request_quote_submit',
        currency: 'SEK',
        value: listPrice,
        quote_id: quote.id,
        items_count: 1,
        ...(product?.id ? { item_id: product.id, item_name: product.name } : {}),
        ...(packageType ? { package_type: packageType } : {}),
        ...(activeEventType ? { event_type: activeEventType } : {}),
        ...(isB2bEvent && b2bFormData.budgetSegment
          ? { budget_segment: b2bFormData.budgetSegment }
          : {}),
        ...(partnerLead.partner ? { partner: partnerLead.partner } : {}),
        ...(partnerLead.cta_context ? { cta_context: partnerLead.cta_context } : {}),
      });

      trackQuoteRequest(product?.id, quote.id);
      trackQuoteFormSubmitted({
        ...analyticsCtx,
        quoteId: quote.id,
        eventType: activeEventType || null,
      });
    } catch (error: unknown) {
      console.error('Error submitting quote request:', error);
      trackQuoteFormError({
        ...analyticsCtx,
        eventType: activeEventType || null,
      }, error);
      setErrors({ submit: 'Ett fel uppstod. Försök igen eller kontakta oss direkt.' });
    } finally {
      setLoading(false);
    }
  };

  const successContent = (
    <div
      className={`${
        isInline
          ? 'bg-slate-900/35 border border-slate-800'
          : 'bg-gray-800 border border-gray-700'
      } rounded-none max-w-md w-full p-8 text-center`}
    >
      <div className="mb-6">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">Tack! Vi har fått er förfrågan.</h2>
        <p className="text-gray-300 mb-4">
          Vi går igenom informationen och återkommer normalt inom 24 timmar.
          {product && (
            <>
              {' '}
              Gäller <strong>{product.name}</strong>.
            </>
          )}
        </p>
        {customerEmailFailed && (
          <p className="text-amber-400 text-sm mb-4">
            Bekräftelsemejl kunde tyvärr inte skickas till{' '}
            {isB2bEvent ? b2bFormData.customerEmail : formData.customerEmail}. Förfrågan är sparad –
            vi återkommer till dig.
          </p>
        )}
        <p className="text-gray-400 text-sm mb-6">
          Behöver vi fler uppgifter hör vi av oss – ni behöver inte ha allt planerat redan nu.
        </p>
      </div>
      {!isInline && (
        <button
          onClick={onClose}
          className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300"
        >
          Stäng
        </button>
      )}
    </div>
  );

  if (success) {
    if (isInline) return successContent;
    return createPortal(
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[210]">
        {successContent}
      </div>,
      document.body
    );
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      handleClose('backdrop');
    }
  };

  const formCard = (
    <div
      className={`${
        isInline
          ? 'bg-slate-900/35 border border-slate-800'
          : 'bg-gray-800 border border-gray-700 max-h-[90vh] overflow-y-auto my-4'
      } rounded-none max-w-3xl w-full`}
    >
      <div
        className={`px-4 py-3 border-b ${
          isInline ? 'border-slate-800' : 'border-gray-700 sticky top-0 bg-gray-800 z-10'
        }`}
      >
        <div className="flex justify-between items-start gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-0.5">
              {isB2bEvent ? 'Eventförfrågan' : 'Berätta om ert event'}
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              {isB2bEvent
                ? 'Fyll i uppgifterna nedan – vi hjälper er dimensionera rätt lösning.'
                : 'Skicka några korta uppgifter så återkommer vi inom 24 timmar med förslag på upplägg och nästa steg.'}
            </p>
            {isInline && !isB2bEvent && (
              <p className="text-slate-500 text-xs mt-2 leading-relaxed">
                Du skickar en offertförfrågan — vi återkommer med förslag och pris. Inget köpkrav.
              </p>
            )}
          </div>
          {!isInline && (
            <button
              type="button"
              onClick={() => handleClose('x')}
              className="text-gray-400 hover:text-white transition-colors shrink-0"
              aria-label="Stäng"
            >
              <X size={22} />
            </button>
          )}
        </div>
      </div>

      <div className="p-4">
        {product && (
          <div className="mb-4 p-2.5 bg-gray-900 rounded-none border border-gray-700">
            <div className="flex items-center gap-2.5">
              <img
                src={product.image}
                alt={product.name}
                className="w-10 h-10 object-cover rounded-none shrink-0"
              />
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white truncate">{product.name}</h3>
                {product.quoteFormMessage && (
                  <p className="text-xs text-purple-400 mt-0.5 line-clamp-2">
                    {product.quoteFormMessage}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
        {packageType && !product && !isB2bEvent && (
          <div className="mb-4 p-2.5 bg-gray-900 rounded-none border border-gray-700">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-none flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-white truncate">{packageType}</h3>
                <p className="text-xs text-gray-400 mt-0.5">Kontext för er förfrågan</p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} onFocusCapture={markFormStarted}>
          {isB2bEvent ? (
            <B2bEventFormSections
              formData={b2bFormData}
              setFormData={setB2bFormData}
              errors={errors}
              dateUndecided={dateUndecided}
              setDateUndecided={setDateUndecided}
              showEndDate={showEndDate}
              setShowEndDate={setShowEndDate}
              setErrors={setErrors}
              markFormStarted={markFormStarted}
              inputClass={inputClass}
              loading={loading}
              submitError={errors.submit}
              showSkaneEventBanner={showSkaneEventBanner}
            />
          ) : (
            <>
          <div className="mb-4">
            <h3 className="text-sm font-bold text-white mb-2 flex items-center">
              <User className="mr-1.5" size={16} />
              Kontaktuppgifter
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">Namn *</label>
                <input
                  type="text"
                  value={formData.customerName}
                  onChange={(e) => {
                    markFormStarted();
                    setFormData({ ...formData, customerName: e.target.value });
                  }}
                  className={inputClass(!!errors.customerName)}
                  placeholder="Ditt namn"
                  autoComplete="name"
                />
                {errors.customerName && (
                  <p className="text-red-400 text-xs mt-0.5">{errors.customerName}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">E-post *</label>
                <input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => {
                    markFormStarted();
                    setFormData({ ...formData, customerEmail: e.target.value });
                  }}
                  className={inputClass(!!errors.customerEmail)}
                  placeholder="din@email.se"
                  autoComplete="email"
                />
                {errors.customerEmail && (
                  <p className="text-red-400 text-xs mt-0.5">{errors.customerEmail}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">Telefon</label>
                <input
                  type="tel"
                  value={formData.customerPhone}
                  onChange={(e) => {
                    markFormStarted();
                    setFormData({ ...formData, customerPhone: e.target.value });
                  }}
                  className={inputClass(!!errors.customerPhone)}
                  placeholder="070-123 45 67"
                  autoComplete="tel"
                />
                {errors.customerPhone && (
                  <p className="text-red-400 text-xs mt-0.5">{errors.customerPhone}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">Datum *</label>
                <DatePicker
                  value={dateUndecided ? '' : formData.eventDate}
                  onChange={(value) => {
                    markFormStarted();
                    setDateUndecided(false);
                    setFormData({ ...formData, eventDate: value });
                  }}
                  minDate={formatDateLocal(new Date())}
                  error={!!errors.eventDate}
                  placeholder={dateUndecided ? 'Datum ej bestämt' : 'Välj datum'}
                />
                <label className="mt-1.5 flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dateUndecided}
                    onChange={(e) => {
                      markFormStarted();
                      const checked = e.target.checked;
                      setDateUndecided(checked);
                      if (checked) {
                        setShowEndDate(false);
                        setFormData((prev) => ({ ...prev, eventDate: '', eventEndDate: '' }));
                        setErrors((prev) => {
                          const next = { ...prev };
                          delete next.eventDate;
                          delete next.eventEndDate;
                          return next;
                        });
                      }
                    }}
                    className="rounded-none border-gray-600"
                  />
                  Datum ännu inte bestämt
                </label>
                {!dateUndecided && (
                  <div className="mt-1.5">
                    {showEndDate ? (
                      <div>
                        <div className="flex items-center justify-between mb-0.5 max-w-xs">
                          <label className="text-xs font-medium text-gray-500">Slutdatum</label>
                          <button
                            type="button"
                            onClick={() => {
                              markFormStarted();
                              setShowEndDate(false);
                              setFormData((prev) => ({ ...prev, eventEndDate: '' }));
                              setErrors((prev) => {
                                const next = { ...prev };
                                delete next.eventEndDate;
                                return next;
                              });
                            }}
                            className="text-xs text-gray-500 hover:text-gray-300"
                          >
                            Ta bort
                          </button>
                        </div>
                        <div className="max-w-xs">
                          <DatePicker
                            value={formData.eventEndDate}
                            onChange={(value) => {
                              markFormStarted();
                              setFormData({ ...formData, eventEndDate: value });
                            }}
                            minDate={formData.eventDate || formatDateLocal(new Date())}
                            error={!!errors.eventEndDate}
                            placeholder="Slutdatum"
                          />
                        </div>
                        {errors.eventEndDate && (
                          <p className="text-red-400 text-xs mt-0.5">{errors.eventEndDate}</p>
                        )}
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          markFormStarted();
                          setShowEndDate(true);
                        }}
                        className="text-xs text-purple-400 hover:text-purple-300"
                      >
                        + Lägg till slutdatum
                      </button>
                    )}
                  </div>
                )}
                {errors.eventDate && (
                  <p className="text-red-400 text-xs mt-0.5">{errors.eventDate}</p>
                )}
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold text-white mb-2 flex items-center">
              <Calendar className="mr-1.5" size={16} />
              Om eventet
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">Ort / plats</label>
                <input
                  type="text"
                  value={formData.customerCity}
                  onChange={(e) => {
                    markFormStarted();
                    setFormData({ ...formData, customerCity: e.target.value });
                  }}
                  className={inputClass()}
                  placeholder="T.ex. Malmö, Lund eller eventlokal om ni vet"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-0.5">Antal gäster</label>
                <input
                  type="number"
                  value={formData.guestCount}
                  onChange={(e) => {
                    markFormStarted();
                    setFormData({ ...formData, guestCount: e.target.value });
                  }}
                  min="1"
                  className={inputClass()}
                  placeholder="T.ex. 80"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-xs font-medium text-gray-400 mb-1">Typ av event</label>
                <div className="flex flex-wrap gap-1.5">
                  {EVENT_TYPES.map((type) => {
                    const selected = formData.eventType === type;
                    return (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          markFormStarted();
                          setFormData({
                            ...formData,
                            eventType: selected ? '' : type,
                          });
                        }}
                        className={`px-2.5 py-1 text-xs border transition-colors ${
                          selected
                            ? 'border-purple-500 bg-purple-600/30 text-white'
                            : 'border-gray-600 text-gray-300 hover:border-gray-400'
                        }`}
                      >
                        {type}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          <div className="mb-4">
            <h3 className="text-sm font-bold text-white mb-2 flex items-center">
              <MessageSquare className="mr-1.5" size={16} />
              Vad behöver ni hjälp med?
            </h3>
            <textarea
              value={formData.message}
              onChange={(e) => {
                markFormStarted();
                setFormData({ ...formData, message: e.target.value });
              }}
              className={inputClass(!!errors.message)}
              rows={3}
              placeholder={messagePlaceholder(formData.eventType)}
            />
            {errors.message && <p className="text-red-400 text-xs mt-0.5">{errors.message}</p>}
          </div>

          {errors.submit && (
            <div className="mb-3 p-2.5 bg-red-500/20 border border-red-500/30 rounded-none">
              <p className="text-red-400 text-sm">{errors.submit}</p>
            </div>
          )}

          <div className="space-y-1.5">
            <p className="text-sm text-gray-400 text-center md:text-left">
              Vi återkommer normalt inom 24 timmar.
            </p>
            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2.5">
              {!isInline && (
                <button
                  type="button"
                  onClick={() => handleClose('cancel')}
                  className="px-5 py-2.5 border border-gray-600 text-gray-300 rounded-none hover:bg-gray-700 transition-colors"
                >
                  Avbryt
                </button>
              )}
              <button
                type="submit"
                disabled={loading}
                className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold rounded-none transition-all duration-300 flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Skickar...
                  </>
                ) : (
                  <>
                    <Send size={18} className="mr-2" />
                    Få förslag & pris
                  </>
                )}
              </button>
            </div>
          </div>
            </>
          )}
        </form>
      </div>
    </div>
  );

  if (isInline) return formCard;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[210]"
      onClick={handleBackdropClick}
    >
      {formCard}
    </div>,
    document.body
  );
};

export default CustomerQuoteRequestForm;
